import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Mail, Linkedin, Phone, Loader2, ChevronDown, ChevronUp, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '@/components/utils/translations';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import { integrateSequentiallyIntoBody } from '@/lib/integratePersonaAsset';
import {
  loadComposerAvailableAssets,
  isEducationalResourceAsset,
  isOutreachAssetsDebugEnabled,
  OUTREACH_ASSETS_DEBUG_BUILD,
  resolveAllowedEducationalProductContexts,
  getComposerAssetSelectionKey,
} from '@/lib/educationalResources';
import { collectPersonPublicationPathIdCandidates } from '@/lib/personPublicationPathIds';
import { extractOutboxStepIdFromGenerateMessage } from '@/lib/outboxRecommendedSendTime';

const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'https://api.neogtm.com'
  : 'https://api.neogtm.com';

function assetBadgeLabel(type) {
  if (isEducationalResourceAsset({ type })) return 'Reading';
  if (type === 'product_link') return 'Link';
  if (type === 'client_reference') return 'Client ref';
  if (type === 'case_study') return 'Case study';
  return 'Asset';
}

function assetBadgeIcon(type) {
  if (type === 'client_reference') return '🏫';
  if (type === 'case_study') return '📄';
  return null;
}

/** Parse ad-hoc generation metadata from generate-message (or message-ranking-shaped) responses. */
function extractUsedSignalsAndFocus(data) {
  const first = Array.isArray(data?.messages) ? data.messages[0] : null;
  const msg = data?.message && typeof data.message === 'object' ? data.message : null;
  let used_signals = null;
  if (Array.isArray(data?.used_signals)) used_signals = data.used_signals;
  else if (msg && Array.isArray(msg.used_signals)) used_signals = msg.used_signals;
  else if (first && Array.isArray(first.used_signals)) used_signals = first.used_signals;
  let focus = null;
  if (typeof data?.focus === 'string') focus = data.focus;
  else if (msg && typeof msg.focus === 'string') focus = msg.focus;
  else if (first && typeof first.focus === 'string') focus = first.focus;
  let context_signals = null;
  if (data?.context_signals && typeof data.context_signals === 'object') context_signals = data.context_signals;
  else if (msg?.context_signals && typeof msg.context_signals === 'object') context_signals = msg.context_signals;
  else if (first?.context_signals && typeof first.context_signals === 'object') context_signals = first.context_signals;
  let style_sample_id = null;
  if (data?.style_sample_id != null) style_sample_id = data.style_sample_id;
  else if (msg?.style_sample_id != null) style_sample_id = msg.style_sample_id;
  else if (first?.style_sample_id != null) style_sample_id = first.style_sample_id;
  return { used_signals, focus, context_signals, style_sample_id };
}

  export default function OutreachComposer({
    tenantId,
    userId,
    personId,
    publicationPersonIdentity,
    /** Linked account (target_products / vendors) — filters educational resources by product context. */
    accountForProductContext,
    /** Persona — `product_affinity` overrides account for SYS/EXAM filtering. */
    personaForProductContext,
    accountId,
    personName,
    personFirstName,
    personEmail,
    personLinkedin,
    personPhone,
    accountName,
    archetype,
    onDraftCreated,
    /** Pre-fill Custom Context from chatbot compose_via_persona deep-link. */
    initialCustomContext = '',
    /** When true, auto-click Generate ~250ms after pre-fill settles. */
    autoGenerateOnMount = false,
    /** Called after auto-generate fires so the host can strip ?auto_generate=1. */
    onAutoGenerateConsumed,
  }) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [channel, setChannel] = useState('email'); // 'email' | 'linkedin' | 'call'
  const [linkedinSubType, setLinkedinSubType] = useState('dm'); // 'dm' | 'connect'
  const [tone, setTone] = useState('warm');
  const [contextType, setContextType] = useState('auto');
  const [customContext, setCustomContext] = useState('');
  const [prefillReady, setPrefillReady] = useState(false);
  const autoGenerateTriggeredRef = useRef(false);
  const handleGenerateRef = useRef(null);
  const [error, setError] = useState(null);
  const [hasPublicationContext, setHasPublicationContext] = useState(false);
  const publicationsProbeCacheRef = useRef(new Map());
  const [userLanguage, setUserLanguage] = useState('English');
  const [languageOverride, setLanguageOverride] = useState('auto');

  const contextUser = useUser();
  const currentTenant = useCurrentTenant();
  const effectiveTenant = tenantId || resolveEffectiveTenant(
    contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
    currentTenant,
    contextUser,
    'instructure'
  );
  const uid = contextUser?.user_id || contextUser?.id;
  const effectiveUserId = userId || uid;

  const [availableAssets, setAvailableAssets] = useState([]);
  const [selectedAssetUrls, setSelectedAssetUrls] = useState(() => new Set());
  const assetsCacheRef = useRef(new Map());

  // Use shared context for profile (display_name, job_title, company_name) - no direct profile fetch
  const tenantProfile = {
    display_name: contextUser?.display_name || contextUser?.full_name || '',
    job_title: contextUser?.job_title || '',
    company_name: contextUser?.company_name || '',
    profile_photo_url: contextUser?.profile_photo_url || ''
  };
  const identity = contextUser ? { id: contextUser.id, email: contextUser.email, full_name: contextUser.full_name } : null;

  useEffect(() => {
    const ctx = String(initialCustomContext || '').trim();
    if (ctx) {
      setCustomContext(ctx);
      setIsExpanded(true);
    }
    setPrefillReady(true);
  }, [initialCustomContext]);

  useEffect(() => {
    if (!autoGenerateOnMount || !prefillReady || !personId || autoGenerateTriggeredRef.current) {
      return undefined;
    }

    const ctx = String(customContext || initialCustomContext || '').trim();
    if (!ctx) return undefined;

    autoGenerateTriggeredRef.current = true;
    const timer = setTimeout(() => {
      handleGenerateRef.current?.();
      onAutoGenerateConsumed?.();
    }, 250);

    return () => clearTimeout(timer);
  }, [
    autoGenerateOnMount,
    prefillReady,
    personId,
    customContext,
    initialCustomContext,
    onAutoGenerateConsumed,
  ]);

  useEffect(() => {
    const loadSettings = async () => {
      if (!uid) return;
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/settings/personalization?tenant_id=${effectiveTenant}&user_id=${uid}`,
          { credentials: 'include' }
        );
        if (response.ok) {
          const data = await response.json();
          if (data.language) setUserLanguage(data.language);
        }
      } catch {
        // Ignore personalization load errors
      }
    };
    loadSettings();
  }, [uid, effectiveTenant]);

  useEffect(() => {
    // Persona-scoped composer: start each persona in Auto mode.
    setLanguageOverride('auto');
    setContextType('auto');
  }, [personId]);

  useEffect(() => {
    setSelectedAssetUrls(new Set());
  }, [personId]);

  useEffect(() => {
    console.log('[NeoOutreachAssets]', OUTREACH_ASSETS_DEBUG_BUILD, 'composer mounted', {
      personId,
      effectiveTenant,
      effectiveUserId,
      tenantIdProp: tenantId,
      userIdProp: userId,
    });
  }, [personId, effectiveTenant, effectiveUserId, tenantId, userId]);

  useEffect(() => {
    if (!personId || !effectiveTenant || !effectiveUserId) {
      console.warn('[NeoOutreachAssets]', OUTREACH_ASSETS_DEBUG_BUILD, 'assets load skipped', {
        personId: personId || null,
        effectiveTenant: effectiveTenant || null,
        effectiveUserId: effectiveUserId || null,
      });
      setAvailableAssets([]);
      return;
    }
    const allowedPc = resolveAllowedEducationalProductContexts(
      accountForProductContext,
      effectiveTenant,
      personaForProductContext
    );
    const pcKey = allowedPc?.length ? allowedPc.join('+') : 'any';
    const cacheKey = `${effectiveTenant}|${personId}|${pcKey}|assets-v4`;
    if (assetsCacheRef.current.has(cacheKey)) {
      const cached = assetsCacheRef.current.get(cacheKey);
      if (isOutreachAssetsDebugEnabled()) {
        console.log('[NeoOutreachAssets]', OUTREACH_ASSETS_DEBUG_BUILD, 'composer cache hit', {
          cacheKey,
          count: cached?.length ?? 0,
          types: (cached || []).map((a) => a.type),
          readingCount: (cached || []).filter((a) => isEducationalResourceAsset(a)).length,
        });
      }
      setAvailableAssets(cached);
      return;
    }
    if (isOutreachAssetsDebugEnabled()) {
      console.log('[NeoOutreachAssets]', OUTREACH_ASSETS_DEBUG_BUILD, 'composer cache miss, fetching', {
        cacheKey,
        personId,
        effectiveTenant,
        effectiveUserId,
      });
    }
    let cancelled = false;
    (async () => {
      try {
        const assets = await loadComposerAvailableAssets({
          personId,
          personIdentity: publicationPersonIdentity,
          account: accountForProductContext,
          persona: personaForProductContext,
          tenantId: effectiveTenant,
          userId: effectiveUserId,
        });
        if (cancelled) return;
        assetsCacheRef.current.set(cacheKey, assets);
        if (isOutreachAssetsDebugEnabled()) {
          console.log('[NeoOutreachAssets]', OUTREACH_ASSETS_DEBUG_BUILD, 'composer setAvailableAssets', {
            cacheKey,
            count: assets.length,
            types: assets.map((a) => a.type),
            readingCount: assets.filter((a) => isEducationalResourceAsset(a)).length,
          });
        }
        setAvailableAssets(assets);
      } catch (e) {
        console.error('[NeoOutreachAssets]', OUTREACH_ASSETS_DEBUG_BUILD, 'composer fetch error', e);
        if (!cancelled) {
          assetsCacheRef.current.set(cacheKey, []);
          setAvailableAssets([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [personId, publicationPersonIdentity, accountForProductContext, personaForProductContext, effectiveTenant, effectiveUserId]);

  useEffect(() => {
    if (!effectiveTenant) {
      setHasPublicationContext(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const idCandidates = publicationPersonIdentity
        ? await collectPersonPublicationPathIdCandidates(publicationPersonIdentity, {})
        : [personId].map((x) => (x != null ? String(x).trim() : '')).filter(Boolean);
      if (!idCandidates.length) {
        if (!cancelled) setHasPublicationContext(false);
        return;
      }
      const cacheKey = `${effectiveTenant}|${idCandidates.join('|')}|pub-probe`;
      const cached = publicationsProbeCacheRef.current.get(cacheKey);
      if (cached !== undefined) {
        if (!cancelled) setHasPublicationContext(cached);
        return;
      }
      try {
        const uid = effectiveUserId;
        const userQs = uid ? `&user_id=${encodeURIComponent(String(uid))}` : '';
        let has = false;
        for (const pid of idCandidates) {
          if (cancelled) return;
          const r = await fetch(
            `${API_BASE_URL}/api/personas/${encodeURIComponent(pid)}/publications?tenant_id=${encodeURIComponent(effectiveTenant)}&limit=1${userQs}`,
            { credentials: 'include' }
          );
          if (!r.ok) continue;
          const d = await r.json().catch(() => ({}));
          const list = Array.isArray(d.publications)
            ? d.publications
            : Array.isArray(d.data?.publications)
              ? d.data.publications
              : [];
          const h = d.has_publications ?? d.data?.has_publications;
          has =
            list.length > 0 ||
            h === true ||
            h === 1 ||
            (typeof h === 'string' && h.toLowerCase() === 'true');
          if (has) break;
        }
        if (!cancelled) {
          publicationsProbeCacheRef.current.set(cacheKey, has);
          setHasPublicationContext(has);
        }
      } catch {
        if (!cancelled) {
          publicationsProbeCacheRef.current.set(cacheKey, false);
          setHasPublicationContext(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [personId, publicationPersonIdentity, accountForProductContext, personaForProductContext, effectiveTenant, effectiveUserId]);

  useEffect(() => {
    if (!isOutreachAssetsDebugEnabled() || availableAssets.length === 0) return;
    const collateral = availableAssets.filter((a) => !isEducationalResourceAsset(a));
    const reading = availableAssets.filter((a) => isEducationalResourceAsset(a));
    console.log('[NeoOutreachAssets]', OUTREACH_ASSETS_DEBUG_BUILD, 'composer render split', {
      personId,
      total: availableAssets.length,
      collateralCount: collateral.length,
      readingCount: reading.length,
      collateralTypes: collateral.map((a) => a.type),
      readingNames: reading.map((a) => a.name),
    });
  }, [availableAssets, personId]);

  const LANGUAGE_OPTIONS = [
    { value: 'auto', label: 'Auto (detect from country)' },
    { value: 'Arabic', label: 'Arabic' },
    { value: 'Brazilian Portuguese', label: 'Brazilian Portuguese' },
    { value: 'Dutch', label: 'Dutch' },
    { value: 'English', label: 'English' },
    { value: 'French', label: 'French' },
    { value: 'German', label: 'German' },
    { value: 'Greek', label: 'Greek' },
    { value: 'Italian', label: 'Italian' },
    { value: 'Portuguese', label: 'Portuguese' },
    { value: 'Spanish', label: 'Spanish' },
    { value: 'Turkish', label: 'Turkish' },
  ];

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      const backendChannel =
        channel === 'linkedin'
          ? (linkedinSubType === 'connect' ? 'linkedin_connect' : 'linkedin_dm')
          : channel;

      const body = {
        tenant_id: tenantId,
        user_id: userId,
        person_id: personId,
        account_id: accountId,
        channel: backendChannel,
        tone,
        context_type:
          contextType === 'publication' && !hasPublicationContext ? 'auto' : contextType,
        custom_context: customContext,
        sender_name: tenantProfile?.display_name || identity?.full_name,
        sender_title: tenantProfile?.job_title,
        sender_company: tenantProfile?.company_name,
      };
      const resolvedLanguageOverride = languageOverride;
      if (resolvedLanguageOverride && resolvedLanguageOverride !== 'auto') {
        body.language = resolvedLanguageOverride;
      }
      // Absolute safety: never allow non-string objects (e.g. click events) into payload.
      if (body.language != null && typeof body.language !== 'string') {
        delete body.language;
      }

      const response = await fetch(`${API_BASE_URL}/api/outbox/generate-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        signal: typeof AbortSignal?.timeout === 'function' ? AbortSignal.timeout(300000) : undefined,
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to generate message');
      }

      const messageContent = data.message ?? data.messages?.[0];
      if (messageContent?.body == null) {
        throw new Error(data.error || 'Failed to generate message');
      }

      const { used_signals, focus, context_signals, style_sample_id } = extractUsedSignalsAndFocus(data);

      const selectedOrdered = availableAssets.filter((a) =>
        selectedAssetUrls.has(getComposerAssetSelectionKey(a))
      );
      const generatedBody = messageContent.body;
      let mergedBody = generatedBody;
      if (selectedOrdered.length > 0) {
        const draftLanguage =
          resolvedLanguageOverride && resolvedLanguageOverride !== 'auto'
            ? resolvedLanguageOverride
            : userLanguage || 'English';
        const { bodyHtml, stopReason } = await integrateSequentiallyIntoBody({
          personId,
          tenantId: effectiveTenant,
          userId: effectiveUserId,
          initialHtml: generatedBody,
          assets: selectedOrdered,
          recipientFirstName: personFirstName || String(personName || '').trim().split(/\s+/)[0] || undefined,
          companyName: accountName || undefined,
          language: draftLanguage,
          channel: backendChannel,
        });
        mergedBody = bodyHtml;
        if (stopReason) {
          const partial = mergedBody !== generatedBody;
          toast.warning(
            partial
              ? 'Some assets could not be woven into the draft; the draft shows what was integrated.'
              : "Couldn't weave those assets into the draft. Try again or add a link manually.",
            { description: 'You can edit the draft below.' }
          );
        }
      }

      const serverStepId = extractOutboxStepIdFromGenerateMessage(data);
      const clientDraftId = serverStepId || `draft-${Date.now()}`;

      // Create draft object for OutboxDrawer
      const draft = {
        id: clientDraftId,
        stepId: clientDraftId,
        step_id: clientDraftId,
        // Person fields - both nested and top-level for compatibility
        persona_id: personId,
        person_id: personId,
        person_name: personName,
        person_email: personEmail,
        linkedin_url: personLinkedin,
        phone: personPhone,
        person: {
          id: personId,
          person_id: personId,
          name: personName,
          full_name: personName,
          first_name: personFirstName,
          last_name: personName?.replace(personFirstName, '').trim() || '',
          email: personEmail,
          linkedin_url: personLinkedin,
          phone: personPhone
        },
        // Account fields - both nested and top-level for compatibility
        account_id: accountId,
        account_name: accountName,
        account: {
          id: accountId,
          account_id: accountId,
          name: accountName,
          account_name: accountName
        },
        // Message fields
        channel: backendChannel,
        subject: messageContent.subject || '',
        body_html: mergedBody,
        body_plain: mergedBody.replace(/<[^>]*>/g, ''),
        status: 'draft_ready',
        archetype,
        language: data?.language_resolution?.final_language || undefined,
        language_resolution: data?.language_resolution || null,
        is_temporary: !serverStepId,
        used_signals,
        focus,
        context_signals,
        style_sample_id,
        // Add tenant info for backend
        tenant_id: tenantId
      };

      onDraftCreated(draft);
      const readyChannelLabel =
        backendChannel === 'linkedin_connect'
          ? 'LinkedIn connection request'
          : backendChannel === 'linkedin_dm'
            ? 'LinkedIn direct message'
            : `${channel.charAt(0).toUpperCase() + channel.slice(1)} message`;
      toast.success('Message generated', {
        description: `${readyChannelLabel} ready to send`
      });
    } catch (err) {
      console.error('Error generating message:', err);
      setError(err.message || 'Failed to generate message');
      toast.error('Generation failed', {
        description: err.message || 'Please try again'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  handleGenerateRef.current = handleGenerate;

  const collateralAssets = availableAssets.filter((a) => !isEducationalResourceAsset(a));
  const readingAssets = availableAssets.filter((a) => isEducationalResourceAsset(a));

  const renderAssetRow = (asset, idx, keyPrefix) => {
    const selectionKey = getComposerAssetSelectionKey(asset);
    const checked = selectionKey ? selectedAssetUrls.has(selectionKey) : false;
    const icon = assetBadgeIcon(asset.type);
    return (
      <li key={`${keyPrefix}-${selectionKey || idx}-${idx}`}>
        <label className="flex items-center gap-2 py-1 pr-0.5 text-xs text-[#0A1A2F] cursor-pointer hover:bg-[#00C2C2]/5 rounded-sm">
          <Checkbox
            checked={checked}
            onCheckedChange={(next) => {
              if (!selectionKey) return;
              setSelectedAssetUrls((prev) => {
                const nextSet = new Set(prev);
                if (next) nextSet.add(selectionKey);
                else nextSet.delete(selectionKey);
                return nextSet;
              });
            }}
            className="shrink-0"
          />
          {icon ? (
            <span className="shrink-0" aria-hidden>
              {icon}
            </span>
          ) : null}
          <span className="flex-1 min-w-0 truncate font-medium">{asset.name || 'Link'}</span>
          <Badge variant="outline" className="text-[9px] px-1 py-0 h-5 shrink-0 font-normal border-gray-200 text-gray-600">
            {assetBadgeLabel(asset.type)}
          </Badge>
        </label>
      </li>
    );
  };

  return (
    <Card className="border-[#00C2C2] bg-gradient-to-br from-[#ECFFFE] to-white">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#00C2C2]" />
            {t('aiOutreach')}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="h-6 w-6 p-0"
          >
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </Button>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="space-y-4">
          {/* Channel Selection */}
          <div>
            <Label className="text-xs text-gray-600 mb-2 block">{t('channel')}</Label>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={channel === 'email' ? 'default' : 'outline'}
                onClick={() => setChannel('email')}
                className={`flex-1 min-w-0 ${channel === 'email' ? 'bg-[#00C2C2] hover:bg-[#00A3A3]' : ''}`}
              >
                <Mail className="w-3 h-3 mr-1 flex-shrink-0" />
                <span className="truncate">{t('email')}</span>
              </Button>
              <Button
                size="sm"
                variant={channel === 'linkedin' ? 'default' : 'outline'}
                onClick={() => setChannel('linkedin')}
                className={`flex-1 min-w-0 ${channel === 'linkedin' ? 'bg-[#00C2C2] hover:bg-[#00A3A3]' : ''}`}
              >
                <Linkedin className="w-3 h-3 mr-1 flex-shrink-0" />
                <span className="truncate">{t('linkedin')}</span>
              </Button>
              <Button
                size="sm"
                variant={channel === 'call' ? 'default' : 'outline'}
                onClick={() => setChannel('call')}
                className={`flex-1 min-w-0 ${channel === 'call' ? 'bg-[#00C2C2] hover:bg-[#00A3A3]' : ''}`}
              >
                <Phone className="w-3 h-3 mr-1 flex-shrink-0" />
                <span className="truncate">{t('call')}</span>
              </Button>
            </div>
            {channel === 'linkedin' && (
              <div className="mt-3 flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={linkedinSubType === 'dm' ? 'default' : 'outline'}
                  onClick={() => setLinkedinSubType('dm')}
                  className="flex-1"
                >
                  Direct message
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={linkedinSubType === 'connect' ? 'default' : 'outline'}
                  onClick={() => setLinkedinSubType('connect')}
                  className="flex-1"
                >
                  Connection request
                </Button>
              </div>
            )}
          </div>

          {/* Tone Selection */}
          <div>
            <Label className="text-xs text-gray-600 mb-2 block">{t('tone')}</Label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger className="h-9 text-sm bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="warm">{t('relationshipBuilder')}</SelectItem>
                <SelectItem value="thought_leadership">{t('thoughtLeadership')}</SelectItem>
                <SelectItem value="challenger">{t('challenger')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Context Selection */}
          <div>
            <Label className="text-xs text-gray-600 mb-2 block">{t('context')}</Label>
            <Select value={contextType} onValueChange={setContextType}>
              <SelectTrigger className="h-9 text-sm bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">{t('autoDetect')}</SelectItem>
                <SelectItem value="persona_initiative">{t('personaInitiative')}</SelectItem>
                <SelectItem value="institutional_change">{t('institutionalChange')}</SelectItem>
                <SelectItem value="strategic_priority">{t('strategicPriority')}</SelectItem>
                <SelectItem value="procurement_signal">{t('procurementSignal')}</SelectItem>
                {hasPublicationContext && (
                  <SelectItem value="publication">{t('publicationContext')}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-gray-600 mb-2 block">Language</Label>
            <Select value={languageOverride} onValueChange={setLanguageOverride}>
              <SelectTrigger className="h-9 text-sm bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Custom Context */}
          <div>
            <Label className="text-xs text-gray-600 mb-2 block">{t('customContext')}</Label>
            <Textarea
              value={customContext}
              onChange={(e) => setCustomContext(e.target.value)}
              placeholder={t('addContext')}
              rows={3}
              className="text-sm resize-none bg-white"
            />
          </div>

          {availableAssets.length > 0 && (
            <div className="rounded-md border border-[#00C2C2]/25 bg-white/70 px-2 py-1.5">
              <div className="flex items-center gap-1.5 text-[11px] font-medium text-[#0A1A2F] mb-1 min-w-0">
                <Link2 className="w-3 h-3 text-[#00A3A3] shrink-0" aria-hidden />
                <span className="truncate">{t('collateralToShare')}</span>
              </div>
              <ul
                className={
                  availableAssets.length > 5
                    ? 'max-h-40 overflow-y-auto divide-y divide-gray-100/90 pr-0.5'
                    : 'divide-y divide-gray-100/90'
                }
              >
                {readingAssets.map((asset, idx) => renderAssetRow(asset, idx, 'reading'))}
                {collateralAssets.map((asset, idx) => renderAssetRow(asset, idx, 'collateral'))}
              </ul>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
              {error}
            </div>
          )}

          {/* Generate Button */}
          <Button
            id="generate-message-btn"
            onClick={() => handleGenerate()}
            disabled={isGenerating}
            className="w-full bg-[#00C2C2] hover:bg-[#00A3A3] text-white"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('generating')}
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                {t('generateMessage')}
              </>
            )}
          </Button>
        </CardContent>
      )}
    </Card>
  );
}